import React, { useEffect, useMemo, useState } from 'react';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useQuery } from 'hooks/useQuery';
import { useLang } from 'models/langContext';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import LicenceStore from 'state/LicenceStore';
import { useRenewLicence } from './hooks';
import { ICaseFormValues } from 'models/cases';
import CaseAddForm, { EAddCaseType } from 'pages/CaseManagementPage/CasesPage/CaseAddForm';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';
import { isPathEqualToRouteName } from 'utils/route';

const LegalEntityLicenceRenewPage = () => {
  const { t } = useTranslations();
  const { pathname, state: routerState } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
  const navigate = useNavigate();
  const query = useQuery();
  const { translations } = useLang();
  const {
    id = '',
    licenceId = '',
    caseId = '',
  } = useParams<{
    id: string;
    licenceId: string;
    caseId: string;
  }>();
  const expand: string | null = query.get('expand');

  const { getLicenceData, licenceDetails, renewLicence } = useRenewLicence();
  const { getCaseData, caseData } = useCase();
  const [type, setType] = useState<LETypes | null>(null);
  const [typeLink, setTypeLink] = useState<ELegalEntityTypes | null>(null);

  useEffect(() => {
    if (licenceDetails && licenceDetails.legal?.type) {
      setType(licenceDetails.legal?.type);
      switch (licenceDetails.legal?.type) {
        case LETypes.individual:
          setTypeLink(ELegalEntityTypes.individual);
          break;
        case LETypes.organization:
          setTypeLink(ELegalEntityTypes.organisation);
          break;
        default:
          break;
      }
    }
  }, [licenceDetails]);

  useEffect(() => {
    (async function getCaseInit() {
      if (caseId) {
        await getCaseData(caseId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const isFromLE = isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityLicenceRenew);
  const isFromLEList = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityLicenceRenewFromLE,
  );
  const isFromLicence = isPathEqualToRouteName(pathname, ERouteLinks.DetailLicenceRenew);
  const isFromLECase = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityCaseLicenceRenewPage,
  );
  const isFromCase = isPathEqualToRouteName(pathname, ERouteLinks.CaseRenewLicencePage);

  useEffect(() => {
    (async function init() {
      if (licenceId) {
        setIsLoading(true);
        const isReturningFromWeaponLinking = (routerState as any)?.saveToStory === true;
        if (!isReturningFromWeaponLinking) {
          LicenceStore.clearStore();
        }
        await getLicenceData(licenceId);
        setIsLoading(false);
      }
    })();
  }, [licenceId]);

  useEffect(() => {
    if (translations) {
      if (isFromLE || isFromLEList || isFromLECase) {
        const breadcrumbsBasic = getBreadcrumbsBasic(
          true,
          typeLink,
          t,
          false,
          licenceDetails?.legal?.uniqueId ?? undefined,
          isFromLE || isFromLEList ? 'licences' : undefined,
        );

        setBreadcrumbs([
          ...breadcrumbsBasic,
          ...(isFromLECase
            ? [
                {
                  label: t(Elabels.caseDetailsHeader),
                  route: `${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                    ':id',
                    id as string,
                  ).replace(':caseId', caseId)}?type=${typeLink}&expand=case`,
                },
                {
                  label: t('legalEntityDetails.view.licence.label'),
                  route: `${ERouteLinks.LegalEntityCaseDetailLicencePage.replace(
                    ':id',
                    id as string,
                  )
                    .replace(':caseId', caseId)
                    .replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`,
                },
              ]
            : isFromLEList
              ? []
              : [
                  {
                    label: t('legalEntityDetails.view.licence.label'),
                    route: `${ERouteLinks.LegalEntityViewLicence.replace(
                      ':id',
                      id as string,
                    ).replace(':licenceId', licenceId)}?type=${typeLink}`,
                  },
                ]),
        ]);
      } else if (isFromLicence) {
        setBreadcrumbs([
          { label: t('module.licences.name'), route: ERouteLinks.LicencesPage },
          {
            label: t('legalEntityDetails.view.licence.label'),
            route: `${ERouteLinks.DetailLicence.replace(':licenceId', licenceId)}?type=${typeLink}`,
          },
        ]);
      } else if (isFromCase) {
        const breadcrumbsBasic = getBreadcrumbsBasic(false, null, t);
        setBreadcrumbs([
          ...breadcrumbsBasic,
          {
            label: t(Elabels.caseDetailsHeader),
            route: `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId as string)}`,
          },
          {
            label: t('legalEntityDetails.view.licence.label'),
            route: `${ERouteLinks.CaseDetailLicencePage.replace(
              ':caseId',
              caseId as string,
            ).replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`,
          },
        ]);
      }
    }
  }, [isFromLE, isFromLEList, expand, id, licenceId, type, translations, licenceDetails]);

  const backLink = () => {
    LicenceStore.clearStore();
    let backUrl = '';
    if (isFromLE || isFromLEList || isFromLECase) {
      if (isFromLECase) {
        backUrl = `${ERouteLinks.LegalEntityCaseDetailLicencePage.replace(':id', id as string)
          .replace(':caseId', caseId)
          .replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`;
      } else if (isFromLEList) {
        backUrl = `${ERouteLinks.ViewLegalEntity.replace(
          ':id',
          id as string,
        )}?type=${typeLink}&expand=licences`;
      } else {
        backUrl = `${ERouteLinks.LegalEntityViewLicence.replace(':id', id as string).replace(
          ':licenceId',
          licenceId,
        )}?type=${typeLink}&expand=${expand}`;
      }
    } else if (isFromLicence) {
      backUrl = `${ERouteLinks.DetailLicence.replace(':licenceId', licenceId)}?type=${typeLink}`;
    } else if (isFromCase) {
      backUrl = `${ERouteLinks.CaseDetailLicencePage.replace(
        ':caseId',
        caseId as string,
      ).replace(':licenceId', licenceId)}?type=${typeLink}&expand=${expand}`;
    }
    navigate(backUrl);
  };

  const submitForm = async (data: ICaseFormValues) => {
    if (licenceDetails) {
      const result = await renewLicence(data, licenceDetails.uniqueId);
      if (result) {
        navigate(
          isFromLE
            ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id as string).replace(':caseId', result as string)}?type=${typeLink}`
            : ERouteLinks.CasesDetailsPage.replace(':caseId', result as string),
        );
      }
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[...breadcrumbs, t('caseManagement.cases.addCase.label')]}
        onClickLink={() => LicenceStore.clearStore()}
      />
      <StyledPageTitle variant="h4">{t('caseManagement.cases.addCase.label')}</StyledPageTitle>
      {!isLoading && licenceDetails && licenceDetails.legal?.uniqueId && type && (
        <CaseAddForm
          LegalEntityid={licenceDetails.legal?.uniqueId}
          licenceId={licenceDetails.uniqueId}
          caseType={EAddCaseType.RENEW}
          type={type}
          isFromLE={isFromLE || isFromLEList}
          submitForm={submitForm}
          backLink={backLink}
          isRenew
        />
      )}
    </>
  );
};

export { LegalEntityLicenceRenewPage };
